const VERSION_STORAGE_KEY = 'barnamegram:app-version';
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

let checkInFlight: Promise<void> | null = null;
let reloadScheduled = false;

async function clearApplicationCaches(): Promise<void> {
  const cleanupTasks: Promise<unknown>[] = [];

  if ('caches' in window) {
    cleanupTasks.push(
      caches.keys().then((cacheNames) => Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))),
    );
  }

  if ('serviceWorker' in navigator) {
    cleanupTasks.push(
      navigator.serviceWorker.getRegistrations().then((registrations) =>
        Promise.all(registrations.map((registration) => registration.unregister())),
      ),
    );
  }

  await Promise.allSettled(cleanupTasks);
}

async function checkVersion(): Promise<void> {
  if (checkInFlight || reloadScheduled) return checkInFlight ?? undefined;

  checkInFlight = (async () => {
    try {
      const response = await fetch(`/version.json?check=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!response.ok) return;

      const payload = (await response.json()) as { version?: unknown };
      if (typeof payload.version !== 'string' || payload.version.length === 0) return;

      const previousVersion = window.localStorage.getItem(VERSION_STORAGE_KEY);
      if (!previousVersion) {
        window.localStorage.setItem(VERSION_STORAGE_KEY, payload.version);
        return;
      }

      if (previousVersion === payload.version) return;

      // Persist before reloading so a transiently stale response cannot cause a reload loop.
      window.localStorage.setItem(VERSION_STORAGE_KEY, payload.version);
      reloadScheduled = true;
      await clearApplicationCaches();
      window.location.reload();
    } catch {
      // Version checks are best-effort: a temporary network/storage failure must not block the app.
    }
  })().finally(() => {
    checkInFlight = null;
  });

  return checkInFlight;
}

export function startVersionControl(): void {
  if (!import.meta.env.PROD) return;

  void checkVersion();
  window.setInterval(() => void checkVersion(), VERSION_CHECK_INTERVAL_MS);
  window.addEventListener('focus', () => void checkVersion());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkVersion();
  });
}
